import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useSuperAdminAuth } from '@/contexts/SuperAdminAuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { LogOut, Users, FileText } from 'lucide-react';
import DashboardStats from '@/components/dashboard/DashboardStats';
import BookingFilters from '@/components/dashboard/BookingFilters';
import BookingTable from '@/components/dashboard/BookingTable';
import SuperAdminControls from '@/components/dashboard/SuperAdminControls';

interface DashboardStats {
  totalShops: number;
  totalBookings: number;
  totalRevenue: number;
  totalCommission: number;
}

interface ShopAnalytics {
  id: string;
  name: string;
  slug: string;
  email: string;
  orderCount: number;
  totalRevenue: number;
  commissionAmount: number;
  netPayout: number;
  paymentStatus: string;
  commissionRate: number;
}

interface BookingRecord {
  id: number;
  "Full Name": string;
  "Phone Number": number;
  "Date": string;
  "Vehicle Make": string;
  "Vehicle Model": string;
  "Oil Brand": string;
  "Oil Viscosity": string;
  "Liters": number;
  "Notes": string;
  "Payment Method": string;
  "Payment Status": string;
  "Shop ID": number;
  "Shop Name": string;
  booking_status: string;
  amount: number;
  created_at: string;
}

const SuperAdmin = () => {
  // ALL HOOKS MUST BE DECLARED FIRST - BEFORE ANY CONDITIONAL LOGIC
  const { user, logout } = useSuperAdminAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalShops: 0,
    totalBookings: 0,
    totalRevenue: 0,
    totalCommission: 0,
  });
  const [shopAnalytics, setShopAnalytics] = useState<ShopAnalytics[]>([]);
  const [allBookings, setAllBookings] = useState<BookingRecord[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<BookingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState<'overview' | 'bookings'>('overview');
  const [shopFilter, setShopFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');

  // Define all functions after hooks but before effects and conditional returns
  const fetchDashboardData = async () => {
    try {
      // Fetch total shops
      const { data: shopsData, error: shopsError } = await supabase
        .from('shops')
        .select('*');

      if (shopsError) throw shopsError;

      // Fetch all bookings
      const { data: bookingsData, error: bookingsError } = await supabase
        .from('New Bookings')
        .select('*')
        .order('created_at', { ascending: false });

      if (bookingsError) throw bookingsError;

      setAllBookings(bookingsData || []);

      // Calculate dashboard stats
      const totalShops = shopsData?.length || 0;
      const totalBookings = bookingsData?.length || 0;
      
      const paidBookings = bookingsData?.filter(b => b["Payment Status"] === 'paid') || [];
      const totalRevenue = paidBookings.reduce((sum, booking) => sum + (booking.amount || 0), 0);
      const totalCommission = paidBookings.reduce((sum, booking) => {
        const shop = shopsData?.find(s => s.name === booking["Shop Name"]);
        const commissionRate = shop?.commission_rate || 10;
        return sum + ((booking.amount || 0) * (commissionRate / 100));
      }, 0);

      setStats({
        totalShops,
        totalBookings,
        totalRevenue,
        totalCommission,
      });

      // Calculate shop analytics
      const shopStats = shopsData?.map(shop => {
        const shopBookings = bookingsData?.filter(booking => booking["Shop Name"] === shop.name) || [];
        const paidShopBookings = shopBookings.filter(b => b["Payment Status"] === 'paid');
        const shopRevenue = paidShopBookings.reduce((sum, booking) => sum + (booking.amount || 0), 0);
        const commissionAmount = shopRevenue * (shop.commission_rate || 10) / 100;
        const netPayout = shopRevenue - commissionAmount;
        
        return {
          id: shop.id,
          name: shop.name,
          slug: shop.slug,
          email: shop.email,
          orderCount: shopBookings.length,
          totalRevenue: shopRevenue,
          commissionAmount: commissionAmount,
          netPayout: netPayout,
          paymentStatus: shop.payment_status || 'pending',
          commissionRate: shop.commission_rate || 10,
        };
      }) || [];

      setShopAnalytics(shopStats);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('فشل في تحميل بيانات لوحة التحكم');
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    const channel = supabase
      .channel('admin-bookings')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'New Bookings'
        },
        () => {
          fetchDashboardData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const filterBookings = () => {
    let filtered = [...allBookings];

    if (shopFilter !== 'all') {
      filtered = filtered.filter(booking => booking["Shop Name"] === shopFilter);
    }

    if (paymentFilter !== 'all') {
      filtered = filtered.filter(booking => booking["Payment Status"] === paymentFilter);
    }

    if (dateFilter) {
      filtered = filtered.filter(booking => 
        booking["Date"] && new Date(booking["Date"]).toDateString() === new Date(dateFilter).toDateString()
      );
    }

    setFilteredBookings(filtered);
  };

  const handleStatClick = (statType: string) => {
    setActiveView('bookings');
    if (statType === 'revenue') {
      setPaymentFilter('paid');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Effects after all hooks and functions are declared
  useEffect(() => {
    if (user) {
      fetchDashboardData();
      const cleanup = setupRealtimeSubscription();
      return cleanup;
    }
  }, [user]);

  useEffect(() => {
    filterBookings();
  }, [allBookings, shopFilter, paymentFilter, dateFilter]);

  // NOW we can do conditional returns - AFTER all hooks have been called
  if (!user) {
    return <Navigate to="/super-admin-login" replace />;
  }

  const uniqueShopNames = [...new Set(allBookings.map(booking => booking["Shop Name"]))];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-autocare-blue"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-autocare-gray-light">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="page-container py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-autocare-blue truncate">لوحة تحكم المدير العام</h1>
              <p className="text-sm sm:text-base text-gray-600 hidden sm:block">تحليلات شاملة للأعمال والإدارة</p>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <div className="flex bg-gray-100 rounded-lg">
                <Button
                  variant={activeView === 'overview' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveView('overview')}
                  className="text-xs sm:text-sm px-2 sm:px-3"
                >
                  نظرة عامة
                </Button>
                <Button
                  variant={activeView === 'bookings' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveView('bookings')}
                  className="text-xs sm:text-sm px-2 sm:px-3"
                >
                  الحجوزات
                </Button>
              </div>
              <SuperAdminControls onLogout={logout} />
            </div>
          </div>
        </div>
      </div>

      <div className="page-container py-8 space-y-8">
        <DashboardStats
          totalBookings={stats.totalBookings}
          totalRevenue={stats.totalRevenue}
          totalCommission={stats.totalCommission}
          netPayout={stats.totalRevenue - stats.totalCommission}
          onStatClick={handleStatClick}
        />

        {activeView === 'overview' ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                تحليلات أداء الورش
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>اسم الورشة</TableHead>
                    <TableHead>الحجوزات</TableHead>
                    <TableHead>الإيرادات</TableHead>
                    <TableHead>معدل العمولة</TableHead>
                    <TableHead>العمولة</TableHead>
                    <TableHead>صافي الدفع</TableHead>
                    <TableHead>حالة الدفع</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shopAnalytics.map((shop) => (
                    <TableRow key={shop.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{shop.name}</p>
                          <p className="text-sm text-gray-500">{shop.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{shop.orderCount}</TableCell>
                      <TableCell>${shop.totalRevenue.toFixed(2)}</TableCell>
                      <TableCell>{shop.commissionRate}%</TableCell>
                      <TableCell>${shop.commissionAmount.toFixed(2)}</TableCell>
                      <TableCell>${shop.netPayout.toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(shop.paymentStatus)}>
                          {shop.paymentStatus === 'paid' ? 'مدفوع' : 
                           shop.paymentStatus === 'pending' ? 'في الانتظار' : 
                           shop.paymentStatus === 'failed' ? 'فشل' : shop.paymentStatus}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ) : (
          <>
            <BookingFilters
              shopFilter={shopFilter}
              paymentFilter={paymentFilter}
              dateFilter={dateFilter}
              shopNames={uniqueShopNames}
              onShopFilterChange={setShopFilter}
              onPaymentFilterChange={setPaymentFilter}
              onDateFilterChange={setDateFilter}
            />

            <BookingTable
              bookings={filteredBookings}
              loading={false}
              onBookingUpdate={fetchDashboardData}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default SuperAdmin;
